import { useState, useEffect } from "react";
import { getProviderLogo } from "../services/providerService";

/**
 * Custom hook to fetch and manage provider logo
 * @param {string} providerId - The provider ID to fetch logo for
 * @returns {string|null} - Blob URL of the logo or null
 */
export const useProviderLogo = (providerId) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!providerId) return;

    let currentLogoUrl = null;

    const fetchLogo = async () => {
      const url = await getProviderLogo(providerId);
      if (url) {
        currentLogoUrl = url;
        setLogoUrl(url);
      }
    };

    fetchLogo();

    // Cleanup blob URL on unmount
    return () => {
      if (currentLogoUrl) {
        URL.revokeObjectURL(currentLogoUrl);
      }
    };
  }, [providerId]);

  return logoUrl;
};

/**
 * Custom hook to fetch and manage multiple provider logos
 * @param {Array<{id: string}>} providers - Array of providers with id field
 * @returns {Object} - Map of provider_id to blob URL
 */
export const useProviderLogos = (providers) => {
  const [logoMap, setLogoMap] = useState({});

  useEffect(() => {
    if (!providers || providers.length === 0) return;

    let currentLogoMap = {};

    const fetchLogos = async () => {
      // Fetch logos for all providers
      await Promise.all(
        providers.map(async (provider) => {
          const logoUrl = await getProviderLogo(provider.id);
          if (logoUrl) {
            currentLogoMap[provider.id] = logoUrl;
          }
        })
      );

      setLogoMap(currentLogoMap);
    };

    fetchLogos();

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(currentLogoMap).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);

  return logoMap;
};
